import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from 'react';
import { computeVisibleRange, isNearEnd, totalSize } from './virtual';

export interface VirtualHandle {
  /** Scrolls so `index` is fully within the viewport (used by keyboard navigation moving the
   * roving-tabindex selection past the currently-rendered window — DESIGN.md §6.1).
   *
   * MVP-433: `align: 'start'` puts the item's own row AT the top instead, which is a different
   * question — "keep reading from here" rather than "reveal this". The default `'nearest'` is
   * deliberately a no-op when the item is already visible, and that is exactly wrong for
   * re-anchoring after the row geometry changed underneath the reader. */
  scrollToIndex: (index: number, align?: 'nearest' | 'start') => void;
}

export interface VirtualListProps<T> {
  items: readonly T[];
  /** Constant row height in px (DESIGN.md §4.2 — never measured at runtime). */
  itemHeight: number;
  renderItem: (item: T, index: number) => ReactNode;
  getKey: (item: T, index: number) => string | number;
  overscan?: number;
  className?: string;
  /** Fires with the index nearest the top of the viewport whenever a scroll changes it — e.g. to
   * drive a floating section-index badge (AC-UI-2). This component has no concept of "sections". */
  onVisibleIndexChange?: (index: number) => void;
  /** MVP-230: fires once each time the rendered window newly comes within `endThreshold` items of
   * the last one — the hook an infinite-scrolling caller loads its next page from. It also fires
   * when a short list never scrolls at all (every item rendered from the start), which is what
   * keeps a page smaller than the viewport from stalling at "there is more, but nothing asks".
   *
   * Fires on the WINDOW, not on a scroll event, so it is equally reached by a keyboard user whose
   * roving cursor walked to the end and by a mouse user who flicked there. The caller owns
   * "already loading" / "no more pages" / "the last page failed" — this component only reports
   * where the window is. */
  onEndReached?: () => void;
  /** How many items from the end counts as "near". Default 8 = ~2 screens of a 44px row at 800px,
   * so the next page is usually in flight before the operator can scroll into the gap. */
  endThreshold?: number;
}

function VirtualListInner<T>(
  { items, itemHeight, renderItem, getKey, overscan = 4, className, onVisibleIndexChange, onEndReached, endThreshold = 8 }: VirtualListProps<T>,
  ref: React.ForwardedRef<VirtualHandle>
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const lastReported = useRef<number | null>(null);

  // Measured on resize (ResizeObserver), never on scroll — the distinction DESIGN.md §4.2's
  // "never measured at runtime" rule actually cares about (see tokens.css's --tile-meta-h note).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setViewportHeight(el.clientHeight);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setViewportHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex(index: number, align: 'nearest' | 'start' = 'nearest') {
        const el = containerRef.current;
        if (!el) return;
        const top = index * itemHeight;
        const bottom = top + itemHeight;
        if (align === 'start') el.scrollTop = top;
        else if (top < el.scrollTop) el.scrollTop = top;
        else if (bottom > el.scrollTop + el.clientHeight) el.scrollTop = bottom - el.clientHeight;
      },
    }),
    [itemHeight]
  );

  const range = useMemo(
    () => computeVisibleRange(scrollTop, viewportHeight, items.length, itemHeight, overscan),
    [scrollTop, viewportHeight, items.length, itemHeight, overscan]
  );

  // Read through a ref so a caller passing an inline arrow (the normal case) does not re-arm this
  // effect on every render — the deps are the window position alone, which is what "newly reached
  // the end" actually means.
  const endReachedRef = useRef(onEndReached);
  endReachedRef.current = onEndReached;
  useEffect(() => {
    if (isNearEnd(range.endIndex, items.length, endThreshold)) endReachedRef.current?.();
  }, [range.endIndex, items.length, endThreshold]);

  function handleScroll(e: UIEvent<HTMLDivElement>) {
    const top = e.currentTarget.scrollTop;
    setScrollTop(top);
    if (onVisibleIndexChange) {
      const visibleIndex = Math.min(items.length - 1, Math.max(0, Math.floor(top / itemHeight)));
      if (visibleIndex !== lastReported.current) {
        lastReported.current = visibleIndex;
        onVisibleIndexChange(visibleIndex);
      }
    }
  }

  const visible = items.slice(range.startIndex, range.endIndex);

  return (
    <div ref={containerRef} className={['virtual-scroll', className].filter(Boolean).join(' ')} onScroll={handleScroll} data-testid="virtual-list">
      <div className="virtual-scroll__spacer" style={{ height: totalSize(items.length, itemHeight) }}>
        <div className="virtual-scroll__window" style={{ transform: `translateY(${range.offsetTop}px)` }}>
          {visible.map((item, i) => {
            const index = range.startIndex + i;
            return (
              <div key={getKey(item, index)} className="virtual-scroll__item" style={{ height: itemHeight }}>
                {renderItem(item, index)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** DESIGN.md §9.2 `VirtualList` — generic fixed-row-height virtualisation, no domain knowledge.
 * Renders a constant DOM node count regardless of `items.length` (AC-UI-1). */
export const VirtualList = forwardRef(VirtualListInner) as <T>(
  props: VirtualListProps<T> & { ref?: React.ForwardedRef<VirtualHandle> }
) => ReturnType<typeof VirtualListInner>;
